import { localize } from "@gtmhub/localization";
import { capitalize } from "@gtmhub/util";
import {
  GridFilterDefinitions,
  GridSortingDefinitions,
  IGridFilter,
  IGridFilterDefinition,
  IGridSortingRule,
  RestLayerFilterFactory,
  RestLayerSortingFactory,
  toGridDefinitionMap,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { UIAutomationStatus } from "../models/automation";
import { RestLayerFilter } from "@gtmhub/core";

enum TriggerFilterOptions {
  goalCreated = "goalCreated",
  goalEdited = "goalEdited",
  objectiveProgressIsUpdated = "objectiveProgressIsUpdated",
  metricCreated = "metricCreated",
  metricEdited = "metricEdited",
  metricProgressIsUpdated = "metricProgressIsUpdated",
  scheduled = "scheduled",
  manual = "manual",
  missingAction = "missingAction",
  missingCondition = "missingCondition",
  missingTrigger = "missingTrigger",
}

export const automationGridFilterDefinitions = (): GridFilterDefinitions => {
  const filterDefinitions: IGridFilterDefinition[] = [
    {
      dataType: "text",
      fieldName: "name",
      displayName: localize("name_cap"),
      trackingName: "Name",
      defaultOperator: "contains",
      supportedOperators: ["equals", "notEqual", "contains", "doesNotContain"],
    },
    {
      fieldName: "ownerId",
      displayName: localize("owner_cap"),
      trackingName: "Owner",
      dataType: "array",
      arrayType: "assignee",
      defaultOperator: "isOneOf",
      supportedOperators: ["isOneOf"],
      unique: true,
    },
    {
      fieldName: "status",
      displayName: localize("status_cap"),
      trackingName: "Status",
      dataType: "array",
      arrayType: "static",
      defaultOperator: "isOneOf",
      supportedOperators: ["isOneOf"],
      entries: [
        {
          displayName: capitalize(localize("enabled")),
          value: UIAutomationStatus.enabled,
        },
        {
          displayName: capitalize(localize("disabled")),
          value: UIAutomationStatus.disabled,
        },
        {
          displayName: capitalize(localize("execute_automation")),
          value: UIAutomationStatus.execute,
        },
        {
          displayName: capitalize(localize("draft")),
          value: UIAutomationStatus.draft,
        },
      ],
      placeholder: localize("search_criteria"),
      unique: true,
    },
    {
      fieldName: "trigger",
      displayName: localize("trigger"),
      trackingName: "Trigger",
      dataType: "array",
      arrayType: "static",
      defaultOperator: "isOneOf",
      supportedOperators: ["isOneOf"],
      entries: [
        {
          displayName: localize("goal_is_created"),
          value: TriggerFilterOptions.goalCreated,
        },
        {
          displayName: localize("goal_is_edited"),
          value: TriggerFilterOptions.goalEdited,
        },
        {
          displayName: localize("objective_progress_is_updated"),
          value: TriggerFilterOptions.objectiveProgressIsUpdated,
        },
        {
          displayName: localize("metric_is_created"),
          value: TriggerFilterOptions.metricCreated,
        },
        {
          displayName: localize("metric_is_edited"),
          value: TriggerFilterOptions.metricEdited,
        },
        {
          displayName: localize("metric_progress_is_updated"),
          value: TriggerFilterOptions.metricProgressIsUpdated,
        },
        {
          displayName: localize("manual"),
          value: TriggerFilterOptions.manual,
        },
        {
          displayName: localize("scheduled"),
          value: TriggerFilterOptions.scheduled,
        },
        {
          displayName: localize("missing_action"),
          value: TriggerFilterOptions.missingAction,
        },
        {
          displayName: localize("missing_condition"),
          value: TriggerFilterOptions.missingCondition,
        },
        {
          displayName: localize("missing_trigger"),
          value: TriggerFilterOptions.missingTrigger,
        },
      ],
      placeholder: localize("search_criteria"),
      unique: true,
    },
  ];

  return toGridDefinitionMap(filterDefinitions);
};

export const automationGridSortingDefinitions = (): GridSortingDefinitions =>
  toGridDefinitionMap([
    {
      fieldName: "name",
      displayName: localize("name"),
      trackingName: "Name",
    },
    {
      fieldName: "status",
      displayName: localize("status"),
      trackingName: "Status",
    },
    {
      fieldName: "trigger",
      displayName: localize("trigger"),
      trackingName: "Trigger",
    },
    {
      fieldName: "assigneeName",
      displayName: localize("owner_cap"),
      trackingName: "Owner",
    },
  ]);

export const filtersOverrideForAutomations: Record<string, RestLayerFilterFactory> = {
  status: (filter: IGridFilter): RestLayerFilter | undefined => {
    const valueMap: Record<UIAutomationStatus, RestLayerFilter> = {
      [UIAutomationStatus.enabled]: {
        $and: [{ status: UIAutomationStatus.enabled }, { isDraft: false }],
      },
      [UIAutomationStatus.disabled]: {
        $and: [{ status: UIAutomationStatus.disabled }, { isDraft: false }],
      },
      [UIAutomationStatus.execute]: {
        $and: [{ status: UIAutomationStatus.execute }, { isDraft: false }],
      },
      [UIAutomationStatus.draft]: { isDraft: true },
    };
    return filterValueToQueryExpression(filter, valueMap);
  },
  trigger: (filter: IGridFilter): RestLayerFilter | undefined => {
    const valueMap: Record<TriggerFilterOptions, RestLayerFilter> = {
      [TriggerFilterOptions.goalCreated]: {
        $and: [{ "trigger.objectType": "goal" }, { "trigger.operation": "created" }],
      },
      [TriggerFilterOptions.goalEdited]: {
        $and: [{ "trigger.objectType": "goal" }, { "trigger.operation": "modified" }],
      },
      [TriggerFilterOptions.objectiveProgressIsUpdated]: {
        $and: [{ "trigger.objectType": "goal" }, { "trigger.operation": "progress changed" }],
      },
      [TriggerFilterOptions.metricCreated]: {
        $and: [{ "trigger.objectType": "metric" }, { "trigger.operation": "created" }],
      },
      [TriggerFilterOptions.metricEdited]: {
        $and: [{ "trigger.objectType": "metric" }, { "trigger.operation": "modified" }],
      },
      [TriggerFilterOptions.metricProgressIsUpdated]: {
        $and: [{ "trigger.objectType": "metric" }, { "trigger.operation": "progress changed" }],
      },
      [TriggerFilterOptions.manual]: { "trigger.manual": true },
      [TriggerFilterOptions.scheduled]: { "trigger.scheduled": true },
      [TriggerFilterOptions.missingAction]: { tasks: { $elemMatch: { actions: null } } },
      [TriggerFilterOptions.missingCondition]: {
        $and: [{ trigger: { $ne: null } }, { tasks: { $size: 0 } }],
      },
      [TriggerFilterOptions.missingTrigger]: { trigger: null },
    };
    return filterValueToQueryExpression(filter, valueMap);
  },
};

function filterValueToQueryExpression<T extends string>(filter: IGridFilter, valueMap: Record<T, RestLayerFilter>): RestLayerFilter | undefined {
  const values = filter.value as string[];
  const expressions = values.reduce((expressions: RestLayerFilter[], value: string): RestLayerFilter[] => {
    const expression = valueMap[value];
    if (expression) {
      expressions.push(expression);
    }
    return expressions;
  }, []);
  if (expressions.length > 0) {
    return {
      $or: expressions,
    };
  }
}

export const sortingOverrideForAutomations: Record<string, RestLayerSortingFactory> = {
  status(sortingRule: IGridSortingRule): string {
    if (sortingRule.reversed) {
      return "isDraft,-status";
    } else {
      // automations marked as draft come first, then disabled, enabled, and execute
      return "-isDraft,status";
    }
  },
  trigger(sortingRule: IGridSortingRule): string {
    if (sortingRule.reversed) {
      return "-trigger.objectType,-trigger.operation,trigger.scheduled,trigger.manual";
    } else {
      return "trigger.objectType,trigger.operation,-trigger.scheduled,-trigger.manual";
    }
  },
};
